import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { QueryBoundary } from '@/components/query-boundary';
import { EmptyState } from '@/components/empty-state';
import { SectionCard } from '@/components/section-card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  buildTrashFilters,
  filterTrashItems,
  getTrashDaysLeft,
  summarizeTrash,
  TRASH_KIND_LABEL,
  type TrashFilterKind,
} from '@/lib/admin-page-models';
import { useTrashList, useRestoreTrash, useDeleteTrash, useEmptyTrash } from '@/lib/queries/trash';
import { AlertTriangle, Clock, RotateCcw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import type { TrashItem } from '@/types/api';
import { useConfirm } from '@/components/confirm';

export const Route = createFileRoute('/trash')({ component: TrashPage });

function TrashPage() {
  const q = useTrashList();
  const restore = useRestoreTrash();
  const del = useDeleteTrash();
  const empty = useEmptyTrash();
  const [filter, setFilter] = useState<TrashFilterKind>('all');
  const confirm = useConfirm();

  async function onRestore(item: TrashItem) {
    try { await restore.mutateAsync(item.id); toast.success('복구됨'); }
    catch (e) { toast.error((e as Error).message); }
  }
  async function onDelete(item: TrashItem) {
    const ok = await confirm({
      title: '영구 삭제',
      description: <>&ldquo;{item.title}&rdquo; 을(를) 완전히 삭제합니다. 복구할 수 없습니다.</>,
      confirmLabel: '영구 삭제',
      tone: 'destructive',
    });
    if (!ok) return;
    try { await del.mutateAsync(item.id); toast.success('영구 삭제됨'); }
    catch (e) { toast.error((e as Error).message); }
  }
  async function onEmpty() {
    const ok = await confirm({
      title: '휴지통 비우기',
      description: '모든 항목을 영구 삭제합니다. 복구할 수 없습니다.',
      confirmLabel: '모두 비우기',
      tone: 'destructive',
    });
    if (!ok) return;
    try { await empty.mutateAsync(); toast.success('비움'); }
    catch (e) { toast.error((e as Error).message); }
  }

  return (
    <>
      <PageHeader title="휴지통" description="삭제된 항목은 30일 후 자동 삭제됩니다" />

      <QueryBoundary
        query={q}
        isEmpty={d => d.length === 0}
        empty={
          <EmptyState title="휴지통이 비어 있습니다." description="삭제된 항목이 생기면 이곳에서 복구하거나 영구 삭제할 수 있습니다." />
        }
      >
        {data => {
          const summary = summarizeTrash(data);
          const filters = buildTrashFilters(data);
          const visible = filterTrashItems(data, filter);

          return (
            <div className="space-y-4">
              <div className="grid grid-cols-2 lg:grid-cols-4 rounded-[var(--radius)] border border-border bg-card overflow-hidden shadow-xs divide-x divide-y divide-border lg:divide-y-0">
                <TrashMetric label="전체" value={summary.total} />
                <TrashMetric label="7일 이내" value={summary.expiringSoon} tone={summary.expiringSoon > 0 ? 'warning' : 'muted'} />
                <TrashMetric label="만료" value={summary.expired} tone={summary.expired > 0 ? 'danger' : 'muted'} />
                <TrashMetric label="메시지" value={summary.byKind.message} />
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="inline-flex min-h-9 items-center rounded-md border border-border bg-card p-0.5 flex-wrap gap-y-0.5 max-w-full">
                  {filters.map(item => (
                    <FilterChip key={item.key} active={filter === item.key} onClick={() => setFilter(item.key)}>
                      {item.label} <span className="font-mono text-[11px] opacity-70">{item.count}</span>
                    </FilterChip>
                  ))}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={onEmpty}
                  disabled={empty.isPending}
                  className="self-start sm:self-auto hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 /> 휴지통 비우기
                </Button>
              </div>

              {visible.length === 0 ? (
                <EmptyState title="해당 종류의 항목이 없습니다." compact />
              ) : (
                <SectionCard title="삭제된 항목" count={visible.length} bodyClassName="p-0">
                  <ul className="divide-y divide-border">
                    {visible.map(item => {
                      const daysLeft = getTrashDaysLeft(item);
                      return (
                        <li key={item.id} className="group flex items-center gap-3 px-5 sm:px-6 py-3.5 hover:bg-accent/40 transition-colors">
                          <Badge variant="secondary" className="shrink-0">{TRASH_KIND_LABEL[item.kind]}</Badge>
                          <div className="flex-1 min-w-0">
                            <div className="text-[14px] font-medium truncate">{item.title}</div>
                            {item.preview && (
                              <div className="mt-0.5 text-[12.5px] text-muted-foreground truncate">{item.preview}</div>
                            )}
                            <div className="mt-1.5 flex items-center gap-2 text-[11.5px] font-mono tabular-nums text-muted-foreground/80">
                              <span>삭제 {new Date(item.deleted_at).toLocaleDateString('ko-KR')}</span>
                              <span className="text-muted-foreground/40">·</span>
                              {daysLeft <= 7 ? (
                                <Badge variant={daysLeft === 0 ? 'destructive' : 'warning'} className="shrink-0">
                                  {daysLeft === 0 ? '만료' : `${daysLeft}일 남음`}
                                </Badge>
                              ) : (
                                <span>{daysLeft}일 후 자동 삭제</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <Button variant="outline" size="sm" onClick={() => onRestore(item)} disabled={restore.isPending}>
                              <RotateCcw /> <span className="hidden sm:inline">복구</span>
                            </Button>
                            <Button
                              variant="ghost" size="sm"
                              className="hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => onDelete(item)}
                              disabled={del.isPending}
                            >
                              <Trash2 /> <span className="hidden sm:inline">영구삭제</span>
                            </Button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </SectionCard>
              )}
            </div>
          );
        }}
      </QueryBoundary>
    </>
  );
}

function TrashMetric({
  label,
  value,
  tone = 'muted',
}: {
  label: string;
  value: number;
  tone?: 'muted' | 'warning' | 'danger';
}) {
  return (
    <div className="p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-medium text-muted-foreground">{label}</span>
        {tone === 'warning' && <Clock className="h-3.5 w-3.5 text-warn" strokeWidth={1.8} />}
        {tone === 'danger' && <AlertTriangle className="h-3.5 w-3.5 text-destructive" strokeWidth={1.8} />}
      </div>
      <div className={tone === 'danger' ? 'mt-2 font-mono text-[24px] font-semibold tabular-nums text-destructive' : tone === 'warning' ? 'mt-2 font-mono text-[24px] font-semibold tabular-nums text-warn' : 'mt-2 font-mono text-[24px] font-semibold tabular-nums'}>
        {value}
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'inline-flex items-center gap-1.5 h-8 px-3 rounded-[5px] text-[12.5px] font-medium transition-colors',
        active
          ? 'bg-foreground text-background'
          : 'text-muted-foreground hover:text-foreground hover:bg-accent/60',
      ].join(' ')}
    >
      {children}
    </button>
  );
}
